import * as React from "react"
import { router } from "@inertiajs/react"
import { wholesale_customers_path, wholesale_customer_path } from "@/lib/routes"
import { useMarket } from "@/lib/useMarket"
import { Button } from "@/components/ui/Button"
import { Input } from "@/components/ui/Input"
import { Textarea } from "@/components/ui/Textarea"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/Dialog"
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/Select"

export type Member = { id: string; name: string }

export type Customer = {
  id: string
  external_id: string
  name: string
  customer_type: "mt" | "gt" | "corp"
  province: string | null
  notes: string | null
  pic_user: Member | null
}

export const TYPE_LABELS: Record<Customer["customer_type"], string> = {
  mt: "MT",
  gt: "GT",
  corp: "Corp",
}

const NO_PIC = "none"

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  customer: Customer | null // null = create
  members: Member[]
  canEditExternalId: boolean
}

export default function CustomerFormDialog({ open, onOpenChange, customer, members, canEditExternalId }: Props) {
  const market = useMarket()
  const [name, setName] = React.useState("")
  const [externalId, setExternalId] = React.useState("")
  const [customerType, setCustomerType] = React.useState<Customer["customer_type"]>("mt")
  const [picUserId, setPicUserId] = React.useState(NO_PIC)
  const [province, setProvince] = React.useState("")
  const [notes, setNotes] = React.useState("")
  const [submitting, setSubmitting] = React.useState(false)

  React.useEffect(() => {
    if (!open) return
    setName(customer?.name ?? "")
    setExternalId(customer?.external_id ?? "")
    setCustomerType(customer?.customer_type ?? "mt")
    setPicUserId(customer?.pic_user?.id ?? NO_PIC)
    setProvince(customer?.province ?? "")
    setNotes(customer?.notes ?? "")
  }, [open, customer])

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    const payload = {
      wholesale_customer: {
        name,
        ...(canEditExternalId ? { external_id: externalId } : {}),
        customer_type: customerType,
        pic_user_id: picUserId === NO_PIC ? "" : picUserId,
        province,
        notes,
      },
    }
    const options = {
      onSuccess: () => onOpenChange(false),
      onFinish: () => setSubmitting(false),
    }
    if (customer) {
      router.patch(wholesale_customer_path(market.slug, customer.id), payload, options)
    } else {
      router.post(wholesale_customers_path(market.slug), payload, options)
    }
  }

  const destroy = () => {
    if (!customer) return
    if (!window.confirm(`Delete ${customer.name}?`)) return
    setSubmitting(true)
    router.delete(wholesale_customer_path(market.slug, customer.id), {
      onSuccess: () => onOpenChange(false),
      onFinish: () => setSubmitting(false),
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{customer ? "Edit customer" : "New customer"}</DialogTitle>
          <DialogDescription>
            {customer ? "Update this wholesale customer." : "Add a wholesale customer to the directory."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-5">
          <div className="flex flex-col gap-1">
            <label className="text-sm">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required />
          </div>

          {(canEditExternalId || customer) && (
            <div className="flex flex-col gap-1">
              <label className="text-sm">External ID</label>
              <Input
                value={externalId}
                onChange={(e) => setExternalId(e.target.value)}
                readOnly={!canEditExternalId}
                disabled={!canEditExternalId}
                placeholder={customer ? undefined : "Leave blank to generate"}
                className="font-mono"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-sm">Type</label>
              <Select value={customerType} onValueChange={(v) => setCustomerType(v as Customer["customer_type"])}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm">PIC</label>
              <Select value={picUserId} onValueChange={setPicUserId}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_PIC}>—</SelectItem>
                  {members.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm">Province</label>
            <Input value={province} onChange={(e) => setProvince(e.target.value)} />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm">Notes</label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
          </div>

          <div className="flex items-center justify-between pt-2">
            {customer ? (
              <Button
                type="button"
                variant="ghost"
                className="text-destructive hover:text-destructive"
                onClick={destroy}
                disabled={submitting}
              >
                Delete
              </Button>
            ) : (
              <span />
            )}
            <Button type="submit" disabled={submitting}>Save</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
